import { useLocation, Outlet } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import Footer from "./Footer";
import PreviewBanner from "@/components/PreviewBanner";
import LenisProvider from "@/components/motion/LenisProvider";
import { useCms } from "@/contexts/CmsContext";

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
};

const pageTransition = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1],
};

export default function Layout() {
  const { isPreview } = useCms();
  const cms = useCms();
  const settings = cms.getSettings();
  const messenger = settings?.messenger || {};
  // Hardcoded: direct Messenger link to Sublime Résine's Facebook Page (overrides CMS snapshot)
  const messengerUrl = "https://m.me/61583444900823";
  const location = useLocation();
  const jsonLd = settings?.jsonLd || {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: settings?.seo?.siteName || "Sublime Résine",
    description: settings?.seo?.defaultDescription || "",
    url: settings?.seo?.baseUrl || "https://sublimeresine.ca",
    address: { "@type": "PostalAddress", addressRegion: "QC", addressCountry: "CA" },
    priceRange: "5$ - 30$",
  };

  return (
    <LenisProvider>
      <div className="min-h-screen flex flex-col bg-[#F2EAF7] relative">
        {/* Noise grain texture overlay */}
        <div
          className="fixed inset-0 pointer-events-none z-[1]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "256px 256px",
            opacity: 0.4,
          }}
        />

        <Helmet>
          <html lang="fr" />
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
        <PreviewBanner />
        <Header />
        <main className={`flex-1 relative z-[2] ${isPreview ? "pt-24 md:pt-28" : "pt-16 md:pt-20"}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="animate"
              variants={pageVariants}
              transition={pageTransition}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
        <Footer />

        {/* Floating Messenger Button */}
        {messenger.isActive !== false && (
          <a
            href={messengerUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="messenger-floating-btn"
            className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-brand-primary text-white rounded-full shadow-[0_8px_30px_rgba(122,63,145,0.35)] transition-all hover:bg-brand-title hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(122,63,145,0.45)]"
            aria-label="Nous contacter sur Messenger"
          >
            <svg viewBox="0 0 36 36" className="w-7 h-7 fill-white">
              <path d="M18 2C9.163 2 2 8.636 2 16.8c0 4.662 2.323 8.82 5.953 11.533V34l5.467-3.013c1.46.406 3.007.613 4.58.613 8.837 0 16-6.636 16-14.8S26.837 2 18 2zm1.6 19.893l-4.074-4.347L7.6 21.893l8.693-9.227 4.074 4.347L28.267 12.6l-8.667 9.293z" />
            </svg>
          </a>
        )}
      </div>
    </LenisProvider>
  );
}
